import React, { useEffect, useState } from "react";
import "./PublishedMenuSection.css";
export const dietaryLabels = {
  gluten_free: "Gluten free",
  vegetarian: "Vegetarian",
  vegan: "Vegan",
};
const matches = (configuration, selected) =>
  configuration &&
  selected.every(
    (tag) =>
      configuration.suitableFor.includes(tag) ||
      (tag === "vegetarian" && configuration.suitableFor.includes("vegan")),
  );
export function matchingConfigurations(item, selected) {
  if (!selected.length) return item.standard ? [item.standard] : item.versions;
  if (matches(item.standard, selected)) return [item.standard];
  return item.versions.filter((version) => matches(version, selected));
}
const fromUrl = () =>
  [
    ...new Set(
      (new URLSearchParams(window.location.search).get("diet") || "").split(
        ",",
      ),
    ),
  ].filter((tag) => Object.hasOwn(dietaryLabels, tag));
const price = (pence) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    pence / 100,
  );
export default function PublishedMenuSection({
  fallback,
  takeawayHref,
  takeawayPhoneNumber,
}) {
  const [data, setData] = useState(null);
  const [loadedPublished, setLoadedPublished] = useState(false);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(fromUrl);
  const [menuId, setMenuId] = useState("");
  useEffect(() => {
    let active = true;
    let controller;
    const load = async () => {
      controller?.abort();
      const requestController = new AbortController();
      controller = requestController;
      const timeout = setTimeout(() => requestController.abort(), 10000);
      try {
        const response = await fetch("/api/menu", {
          cache: "no-store",
          signal: requestController.signal,
        });
        if (!response.ok) throw new Error("Unavailable");
        const body = await response.json();
        if (
          !body.success ||
          typeof body.data?.configured !== "boolean" ||
          !Array.isArray(body.data?.menus)
        )
          throw new Error("Invalid menu");
        if (active) {
          setData(body.data);
          setError(false);
          if (body.data.configured) setLoadedPublished(true);
        }
      } catch (failure) {
        if (active) {
          setError(true);
          setData(null);
        }
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      active = false;
      controller?.abort();
      clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    const update = () => setSelected(fromUrl());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  const changeFilters = (next) => {
    setSelected(next);
    const url = new URL(window.location.href);
    if (next.length) url.searchParams.set("diet", next.join(","));
    else url.searchParams.delete("diet");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  };
  if (!data?.configured) {
    if (loadedPublished)
      return (
        <section className="menu-card" aria-label="Menu">
          <h2>The menu</h2>
          <p role="status">
            {error
              ? "The menu is temporarily unavailable. Please ask us for current dietary options."
              : "No menu items are currently published."}
          </p>
        </section>
      );
    return (
      <>
        {selected.length > 0 && (
          <p role="status" className="menu-note">
            Dietary information is not available for the menu below. Please ask
            us about suitable dishes.
          </p>
        )}
        {fallback}
      </>
    );
  }
  const menu = data.menus.find((menu) => menu.id === menuId) || data.menus[0];
  const sections = (menu?.sections || [])
    .map((section) => ({
      ...section,
      cards: section.items.flatMap((item) =>
        matchingConfigurations(item, selected).map((configuration) => ({
          item,
          configuration,
        })),
      ),
    }))
    .filter((section) => section.cards.length);
  const count = sections.reduce(
    (sum, section) =>
      sum + new Set(section.cards.map((card) => card.item.id)).size,
    0,
  );
  return (
    <section className="menu-card" aria-labelledby="published-menu-heading">
      <div className="menu-header">
        <p className="home-eyebrow">OUR MENUS</p>
        <h2 id="published-menu-heading">The menu</h2>
        <p className="menu-intro">
          Choose a menu and dietary requirements to find suitable dishes.
        </p>
        <p className="menu-note">
          Please inform your server of any allergies before ordering.
        </p>
      </div>
      <div className="menu-tabs" role="group" aria-label="Menu types">
        {data.menus.map((option) => (
          <button
            type="button"
            className={
              option.id === menu?.id ? "menu-tab is-active" : "menu-tab"
            }
            aria-pressed={option.id === menu?.id}
            onClick={() => setMenuId(option.id)}
            key={option.id}
          >
            {option.label}
          </button>
        ))}
      </div>
      <fieldset className="dietary-filters">
        <legend>Dietary requirements</legend>
        {Object.entries(dietaryLabels).map(([tag, label]) => (
          <label key={tag}>
            <input
              type="checkbox"
              checked={selected.includes(tag)}
              onChange={(event) =>
                changeFilters(
                  event.target.checked
                    ? [...selected, tag]
                    : selected.filter((value) => value !== tag),
                )
              }
            />
            {label}
          </label>
        ))}
        {selected.length > 0 && (
          <button type="button" onClick={() => changeFilters([])}>
            Clear filters
          </button>
        )}
      </fieldset>
      <p className="menu-note" role="status">
        {count} {count === 1 ? "dish" : "dishes"}
        {selected.length ? " matching all selected requirements" : " available"}
        .
      </p>
      {!sections.length && (
        <p className="menu-intro">
          {selected.length
            ? "No dishes match this combination. Try changing your filters or ask us about suitable options."
            : "No dishes are currently available."}
        </p>
      )}
      <div className="menu-sections">
        {sections.map((section) => (
          <details
            className="menu-group"
            key={`${menu.id}:${section.id}:${selected.join(",")}`}
            open={selected.length > 0}
          >
            <summary>{section.title}</summary>
            <div className="menu-group-items">
              {section.cards.map(({ item, configuration }) => (
                <article
                  className="menu-item"
                  key={`${item.id}:${configuration.id}`}
                >
                  <div className="menu-item-row">
                    <h3>
                      {item.name}
                      {configuration.id !== "standard" && (
                        <span className="dietary-version-name">
                          {" "}
                          — {configuration.name}
                        </span>
                      )}
                    </h3>
                    <span className="menu-item-price">
                      {configuration.priceFrom ? "From " : ""}
                      {price(configuration.price)}
                    </span>
                  </div>
                  {configuration.description && (
                    <p className="menu-item-description">
                      {configuration.description}
                    </p>
                  )}
                  <ul
                    className="dietary-badges"
                    aria-label={`${item.name} dietary suitability`}
                  >
                    {Object.entries(dietaryLabels)
                      .filter(
                        ([tag]) =>
                          matches(configuration, [tag]) ||
                          (!selected.length &&
                            item.versions.some((version) =>
                              matches(version, [tag]),
                            )),
                      )
                      .map(([tag, label]) => (
                        <li key={tag}>
                          {label}
                          {matches(configuration, [tag])
                            ? configuration.id !== "standard"
                              ? " version"
                              : ""
                            : " option available"}
                        </li>
                      ))}
                  </ul>
                  {configuration.modifierGroups.map((group) => (
                    <div key={group.id} className="dietary-modifier-group">
                      <p>
                        {group.fixed ? "Includes" : group.name}
                        {!group.fixed &&
                          ` (choose ${group.minSelections === group.maxSelections ? group.minSelections : `${group.minSelections}–${group.maxSelections}`})`}
                        :{" "}
                        {group.fixed
                          ? group.options
                              .map((option) => option.name)
                              .join(", ")
                          : ""}
                      </p>
                      {!group.fixed && (
                        <ul className="menu-variants">
                          {group.options.map((option) => (
                            <li className="menu-variant-chip" key={option.id}>
                              {option.name}
                              {option.price > 0
                                ? ` (+${price(option.price)})`
                                : ""}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  {configuration.priceFrom && (
                    <p className="menu-item-note">
                      Base price {price(configuration.basePrice)}. Starting
                      price includes the lowest-priced required choices.
                      Supplements shown are added to the dish’s base price.
                    </p>
                  )}
                </article>
              ))}
            </div>
          </details>
        ))}
      </div>
      {takeawayHref && (
        <a className="takeaway-callout" href={takeawayHref}>
          For takeaway orders, call {takeawayPhoneNumber}
        </a>
      )}
    </section>
  );
}
